import { Alert, Badge, Box, Button, Card, Checkbox, Group, Pagination, SegmentedControl, Select, Skeleton, Stack, Table, Text, TextInput } from "@mantine/core";
import { IconEdit, IconInfoCircle, IconSearch } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { TenantFeePolicyApi, TenantFeePolicyAssignmentApi } from "../../../../apis";
import type { FeePolicyCustomer } from "../../../../apis/tenantFeePolicyAssignment";
import type { FeePolicy } from "../../../../apis/tenantFeePolicy";
import { AssignCustomerDrawer } from "../../../../components/billing/AssignCustomerDrawer";
import { BulkAssignBar } from "../../../../components/billing/BulkAssignBar";
import { PermissionGate } from "../../../../components/billing/PermissionGate";
import { Message } from "../../../../utils/message";
import { formatMoney } from "../../../../utils/format";

const PAGE_SIZE = 25;
const DEFAULT_SENTINEL = "DEFAULT";
const TYPE_ALL = "ALL";

const TYPE_OPTIONS = [
  { value: TYPE_ALL, label: "Бүгд" },
  { value: "BUYER", label: "Худалдан авагч" },
  { value: "SUPPLIER", label: "Нийлүүлэгч" },
];

// Friendly Mongolian label for business type. de_business.business.type is
// BUYER/SUPPLIER in DeHub; the spec's "Merchant/Reseller" is conceptual.
const typeLabel = (type: string): string => {
  switch (type) {
    case "BUYER":
      return "Худалдан авагч";
    case "SUPPLIER":
      return "Нийлүүлэгч";
    default:
      return type || "—";
  }
};

// Active-policy presentation, mirroring the mock's colored badges:
// DEFAULT = violet, NAMED percentage = cyan, NAMED flat = orange.
const policyBadge = (p: FeePolicyCustomer["effectivePolicy"]): { name: string; color: string } | null => {
  if (!p) return null;
  if (p.scope === "DEFAULT") return { name: "DEFAULT", color: "brand" };
  return { name: p.name ?? "(нэргүй)", color: p.calculationMethod === "FLAT" ? "orange" : "cyan" };
};

const policyRate = (p: FeePolicyCustomer["effectivePolicy"]): string => {
  if (!p) return "";
  if (p.calculationMethod === "PERCENTAGE" && p.feeRate) return `${(Number(p.feeRate) * 100).toFixed(2)}%`;
  if (p.calculationMethod === "FLAT" && p.flatFeeAmount) return `FLAT ${formatMoney(p.flatFeeAmount)}`;
  return "";
};

const FeePolicyCustomersTab = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [policyFilter, setPolicyFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>(TYPE_ALL);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [editRow, setEditRow] = useState<FeePolicyCustomer | null>(null);

  // Debounce the server-side name search so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
      setSelected(new Set());
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Server-side roster: ALL end-customers (incl. those on DEFAULT), each with its
  // effective policy. Search + policy filter + pagination all handled by backend.
  const { data, mutate, isLoading } = useSWR(
    ["swr.my.fee-policy-customers", page, policyFilter, typeFilter, debouncedSearch] as const,
    () =>
      TenantFeePolicyAssignmentApi.listCustomers({
        page,
        limit: PAGE_SIZE,
        feePolicyId: policyFilter ?? undefined,
        type: typeFilter === TYPE_ALL ? undefined : (typeFilter as "BUYER" | "SUPPLIER"),
        search: debouncedSearch || undefined,
      })
  );

  const { data: policiesData } = useSWR("swr.my.fee-policies.all", () =>
    TenantFeePolicyApi.list({ page: 1, limit: 200 })
  );

  const rows: FeePolicyCustomer[] = useMemo(() => data?.rows ?? [], [data]);
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const namedPolicies = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawP = policiesData as any;
    const all: FeePolicy[] = Array.isArray(rawP) ? rawP : (rawP?.rows ?? []);
    return all.filter((p) => p.scope === "NAMED" && p.status === "ACTIVE");
  }, [policiesData]);

  // Index page rows by businessId so bulk actions can resolve assignment ids.
  const byBusinessId = useMemo(() => {
    const m = new Map<string, FeePolicyCustomer>();
    for (const r of rows) m.set(r.businessId, r);
    return m;
  }, [rows]);

  const allRowIds = useMemo(() => rows.map((r) => r.businessId), [rows]);
  const allSelected = selected.size > 0 && allRowIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(allRowIds) : new Set());
  };
  const toggleOne = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const onBulkApply = async ({ feePolicyId, effectiveFrom }: { feePolicyId: string | null; effectiveFrom: string }) => {
    try {
      const targets = Array.from(selected).map((id) => byBusinessId.get(id)).filter(Boolean) as FeePolicyCustomer[];
      if (feePolicyId === null) {
        // Revert: only customers with an active NAMED assignment have something to close.
        const ids = targets.map((c) => c.assignmentId).filter(Boolean) as string[];
        await Promise.all(ids.map((id) => TenantFeePolicyAssignmentApi.unassign(id)));
      } else {
        const customerBusinessIds = targets.map((c) => c.businessId);
        await TenantFeePolicyAssignmentApi.assignBulk({ feePolicyId, customerBusinessIds, effectiveFrom });
      }
      setSelected(new Set());
      await mutate();
      Message.success("Бодлого амжилттай оноогдлоо.");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      Message.error(e?.message ?? "Алдаа гарлаа");
    }
  };

  const onRowAssign = async ({ feePolicyId, effectiveFrom }: { feePolicyId: string; effectiveFrom: string }) => {
    if (!editRow) return;
    try {
      await TenantFeePolicyAssignmentApi.assign({
        customerBusinessId: editRow.businessId,
        feePolicyId,
        effectiveFrom,
      });
      setEditRow(null);
      await mutate();
      Message.success("Бодлого амжилттай оноогдлоо.");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      Message.error(e?.message ?? "Алдаа гарлаа");
    }
  };

  const onRowRevert = async () => {
    if (!editRow?.assignmentId) return;
    try {
      await TenantFeePolicyAssignmentApi.unassign(editRow.assignmentId);
      setEditRow(null);
      await mutate();
      Message.success("DEFAULT бодлогод буцаагдлаа.");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      Message.error(e?.message ?? "Алдаа гарлаа");
    }
  };

  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  return (
    <Box pt="md">
      <Stack gap="md">
        <Alert color="brand" icon={<IconInfoCircle size={16} />} variant="light">
          <Text span fw={700}>Бүх харилцагч ба тэдний идэвхтэй бодлого.</Text>{" "}
          Checkbox-ээр сонгож олныг нь нэг policy-д оноо. Эсвэл нэг нэгээр нь Засах товчоор оноо.{" "}
          <Text span c="dimmed">
            (Оноолтгүй харилцагчид DEFAULT бодлого ашиглана.)
          </Text>
        </Alert>

        <Card withBorder radius="md" padding="md">
        <Stack gap="md">
        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Нэр эсвэл ТТД-ээр хайх"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            w={300}
          />
          <Select
            placeholder="Бүх policy"
            data={[
              { value: DEFAULT_SENTINEL, label: "DEFAULT (оноолтгүй)" },
              ...namedPolicies.map((p) => ({ value: p.id, label: p.name ?? "(нэргүй)" })),
            ]}
            value={policyFilter}
            onChange={(v) => {
              setPolicyFilter(v || null);
              setPage(1);
              setSelected(new Set());
            }}
            clearable
            w={220}
          />
          <SegmentedControl
            data={TYPE_OPTIONS}
            value={typeFilter}
            onChange={(v) => {
              setTypeFilter(v);
              setPage(1);
              setSelected(new Set());
            }}
            size="sm"
          />
          <Box ml="auto">
            <Text size="sm" c="dimmed">{total} харилцагч · {selected.size} сонгосон</Text>
          </Box>
        </Group>

        <PermissionGate code="BIL_015">
          <BulkAssignBar
            selectedCount={selected.size}
            namedPolicies={namedPolicies}
            onApply={onBulkApply}
            onCancel={() => setSelected(new Set())}
          />
        </PermissionGate>

        <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="sm" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 48 }}>#</Table.Th>
              <Table.Th style={{ width: 32 }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => toggleAll(e.currentTarget.checked)}
                  aria-label="Бүгдийг сонгох"
                />
              </Table.Th>
              <Table.Th>Харилцагч</Table.Th>
              <Table.Th>ТТД</Table.Th>
              <Table.Th>Төрөл</Table.Th>
              <Table.Th>Идэвхтэй бодлого</Table.Th>
              <Table.Th style={{ width: 80 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                <Table.Tr key={`skeleton-${i}`}>
                  <Table.Td><Skeleton height={16} width={20} /></Table.Td>
                  <Table.Td><Skeleton height={16} width={16} /></Table.Td>
                  <Table.Td><Skeleton height={16} width="60%" /></Table.Td>
                  <Table.Td><Skeleton height={16} width={80} /></Table.Td>
                  <Table.Td><Skeleton height={16} width={90} /></Table.Td>
                  <Table.Td><Skeleton height={16} width={70} /></Table.Td>
                  <Table.Td><Skeleton height={16} width={50} /></Table.Td>
                </Table.Tr>
              ))}
            {!isLoading && rows.map((c, idx) => (
              <Table.Tr
                key={c.businessId}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/my-billing/fee-policy/customers/${c.businessId}`)}>
                <Table.Td>
                  <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {(page - 1) * PAGE_SIZE + idx + 1}
                  </Text>
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selected.has(c.businessId)}
                    onChange={(e) => toggleOne(c.businessId, e.currentTarget.checked)}
                    aria-label={`Сонгох ${c.name ?? c.businessId}`}
                  />
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>{c.name ?? "(нэргүй)"}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {c.regNumber || "—"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">{typeLabel(c.type)}</Text>
                </Table.Td>
                <Table.Td>
                  {(() => {
                    const badge = policyBadge(c.effectivePolicy);
                    const rate = policyRate(c.effectivePolicy);
                    if (!badge) return <Text size="sm" c="dimmed">—</Text>;
                    return (
                      <Group gap="xs">
                        <Badge color={badge.color} size="sm" variant="light">{badge.name}</Badge>
                        {rate && <Text size="sm">{rate}</Text>}
                      </Group>
                    );
                  })()}
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <PermissionGate code="BIL_015">
                    <Button
                      variant="subtle"
                      size="xs"
                      leftSection={<IconEdit size={14} />}
                      onClick={() => setEditRow(c)}
                    >
                      Засах
                    </Button>
                  </PermissionGate>
                </Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center" py="md">
                    {debouncedSearch || policyFilter || typeFilter !== TYPE_ALL
                      ? "Шүүлтэд тохирох харилцагч олдсонгүй."
                      : "Харилцагч одоогоор алга."}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
        </Table.ScrollContainer>

        <Group justify="space-between">
          <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
            {total} харилцагч · {from}-{to} харуулж байна
          </Text>
          {pageCount > 1 && (
            <Pagination
              value={page}
              onChange={(p) => {
                setPage(p);
                setSelected(new Set());
              }}
              total={pageCount}
              size="sm"
            />
          )}
        </Group>
        </Stack>
        </Card>
      </Stack>

      <AssignCustomerDrawer
        opened={!!editRow}
        customer={editRow}
        namedPolicies={namedPolicies}
        onAssign={onRowAssign}
        onRevert={onRowRevert}
        onClose={() => setEditRow(null)}
      />
    </Box>
  );
};

export default FeePolicyCustomersTab;
